import { useState, useRef, useCallback } from 'react';
import { getApiUrl, env } from '../config/env';
import { useAuth } from '../contexts/AuthContext';

export const useSummary = (finalText) => {
  const { authFetch } = useAuth();
  const [currentSummary, setCurrentSummary] = useState('');
  const [summaryVisible, setSummaryVisible] = useState(false);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const autoSummaryTimerRef = useRef(null);

  const generateAutoSummary = useCallback(async () => {
    if (!finalText || finalText.length < env.MIN_TEXT_LENGTH_FOR_SUMMARY) {
      console.log('❌ Text too short for auto summary:', finalText.length);
      return;
    }

    console.log('🤖 Starting automatic summary generation for text:', finalText.substring(0, 100) + '...');
    setIsSummaryLoading(true);

    try {
      const response = await authFetch(getApiUrl('/api/summarize'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: finalText,
          type: 'cumulative' // Use cumulative for auto summaries
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('📋 Summary API response:', data);
      
      if (data.summary) {
        setCurrentSummary(data.summary);
        setSummaryVisible(true);
        console.log('✅ Auto summary generated successfully:', data.summary.substring(0, 100) + '...');
      } else {
        console.log('❌ No summary in API response');
      }
    } catch (error) {
      console.error('❌ Error generating auto summary:', error);
      setCurrentSummary('❌ Automatic summary generation error: ' + error.message);
      setSummaryVisible(true);
    } finally {
      setIsSummaryLoading(false);
    }
  }, [finalText]);

  const generateReport = useCallback(async (showLoadingScreen, updateLoadingProgress, hideLoadingScreen, navigateToEditReport, patientConstantes) => {
    if (!finalText || finalText.length < env.MIN_TEXT_LENGTH_FOR_SUMMARY) {
      alert(`Not enough text to generate a report (minimum ${env.MIN_TEXT_LENGTH_FOR_SUMMARY} characters)`);
      return;
    }

    console.log('Generating final report with constantes:', patientConstantes);

    // Show loading screen
    if (showLoadingScreen) showLoadingScreen();

    const updateProgress = (progress, text) => {
      console.log(`Progress: ${progress}% - ${text}`);
      if (updateLoadingProgress) updateLoadingProgress(progress, text);
    };

    try {
      // Step 1: Analyze transcript
      updateProgress(20, "Analyzing transcription...");
      await new Promise(resolve => setTimeout(resolve, 300));

      // Step 2: Extract structured medical facts
      updateProgress(40, "Extracting medical data...");
      const extractResponse = await authFetch(getApiUrl('/api/extract-report-data'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: finalText,
          patientConstantes: patientConstantes // Pass typed constantes to backend
        }),
      });

      if (!extractResponse.ok) {
        throw new Error(`Fact extraction failed: ${extractResponse.status}`);
      }

      updateProgress(80, "Structuring medical sections...");
      const extractData = await extractResponse.json();

      updateProgress(100, "Report complete!");

      // Create structured report data with extracted facts only
      const reportData = {
        extractedFacts: extractData.extracted_facts,
        originalText: finalText
      };

      // Navigate to React EditReport component with structured data
      setTimeout(() => {
        if (hideLoadingScreen) hideLoadingScreen();
        if (navigateToEditReport) {
          navigateToEditReport(reportData);
        }
      }, 200);

    } catch (error) {
      console.error('Error generating report:', error);
      if (hideLoadingScreen) hideLoadingScreen();
      alert(`❌ Report generation error: ${error.message}`);
    }
  }, [finalText]);

  const startAutoSummary = useCallback(() => {
    // Don't start if already running
    if (autoSummaryTimerRef.current) {
      console.log('Auto-summary timer already running');
      return;
    }
    
    console.log('Starting automatic summary timer');
    autoSummaryTimerRef.current = setInterval(() => {
      if (finalText && finalText.length > env.MIN_TEXT_LENGTH_FOR_SUMMARY) {
        console.log('Auto-generating summary for text length:', finalText.length);
        generateAutoSummary();
      }
    }, env.AUTO_SUMMARY_INTERVAL);
  }, [generateAutoSummary]);

  const stopAutoSummary = useCallback(() => {
    if (autoSummaryTimerRef.current) {
      clearInterval(autoSummaryTimerRef.current);
      autoSummaryTimerRef.current = null;
      console.log('Auto-summary timer stopped');
    }
  }, []);

  return {
    currentSummary,
    summaryVisible,
    setSummaryVisible,
    isSummaryLoading,
    generateReport,
    startAutoSummary,
    stopAutoSummary
  };
};